import { CrunchServiceWithContext } from "@crunchdao/sdk";
import { EnhancedBaseCommand } from "@crunchdao/shared-cli";

export class MarginCommand extends EnhancedBaseCommand {
  private getCrunchService() {
    return CrunchServiceWithContext({
      program: this.program,
    });
  }

  async payout(crunchName: string): Promise<void> {
    try {
      const crunchService = this.getCrunchService();

      const tx = await crunchService.payoutMargin(crunchName);

      this.success("Margin payout completed successfully", {
        crunchName,
        transaction: tx,
      });
    } catch (error) {
      this.handleMarginPayoutError(error, crunchName);
    }
  }

  private handleMarginPayoutError(error: any, crunchName: string): never {
    // Check for invalid crunch state to payout margin error
    const isInvalidCrunchStateForMargin =
      error?.error?.errorCode?.code === "InvalidCrunchStateToPayoutMargin";

    if (isInvalidCrunchStateForMargin) {
      const customError = this.createEnhancedError(
        `Cannot payout margin for crunch "${crunchName}". The crunch must be in "end" state before margin can be paid out.`,
        "InvalidCrunchStateForMarginError",
        "INVALID_CRUNCH_STATE_FOR_MARGIN",
        {
          crunchName,
          errorCode: error.error?.errorCode?.code || "InvalidCrunchStateToPayoutMargin",
          errorNumber: error.error?.errorCode?.number || 6006,
        },
        error
      );

      return this.handleError(customError);
    }

    // Check for foundation wallet ATA not initialized error
    const isFoundationAtaNotInitialized =
      error?.error?.errorCode?.code === "AccountNotInitialized" &&
      error?.error?.origin === "foundation_wallet_ata";

    if (isFoundationAtaNotInitialized) {
      const network = this.resolvedOptions.network || "localnet";

      // Use USDC mint addresses for different networks
      let usdcMintAddress = "";
      if (network === "mainnet") {
        usdcMintAddress = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"; // Mainnet USDC
      } else {
        usdcMintAddress = "B9yyYFe3S6i2LVrZeEpXtyVVMUr4vzrQquzzb7pLsNhy"; // Custom USDC for devnet/localnet
      }

      let createAtaInstructions = "";
      if (network === "localnet" || network === "devnet") {
        createAtaInstructions = `Run: spl-token create-account ${usdcMintAddress} --owner <FOUNDATION_WALLET_ADDRESS>`;
      } else if (network === "mainnet") {
        createAtaInstructions = `Create USDC Associated Token Account for foundation wallet with mint ${usdcMintAddress}`;
      }

      const customError = this.createEnhancedError(
        `Foundation wallet USDC Associated Token Account not initialized. The margin payout requires a USDC token account for the foundation wallet. ${createAtaInstructions}`,
        "FoundationWalletAtaNotInitializedError",
        "FOUNDATION_WALLET_ATA_NOT_INITIALIZED",
        {
          crunchName,
          network,
          createAtaInstructions,
          tokenMint: usdcMintAddress,
          anchorErrorCode: error.error?.errorCode?.code || "AccountNotInitialized",
          anchorErrorNumber: error.error?.errorCode?.number || 3012,
        },
        error
      );

      return this.handleError(customError);
    }

    // Fall back to generic transaction error handling
    return this.handleTransactionError(error, crunchName);
  }
}
